import {
  useEffect,
  useRef,
  useState
} from 'react'
import { useDispatch } from 'react-redux'
import {
  Image,
  Stack,
  Text
} from '@chakra-ui/react'
import { COLORS } from 'utils/styles'
import { ImagesPng } from 'assets/images'
import { motion } from 'framer-motion'
import {
  getComments,
  likeComment,
  setBlogLikeOngoing,
  setBlogParent,
  setComments,
  setProfileItemId,
  showCurrentChatFriendMarkerCoordinate,
  setUserSectionPage
} from 'store'
import {
  CommentIcon,
  EditIcon2,
  InfoSquareButton,
  LikeIcon,
  RepostIcon,
  ShareIcon
} from 'assets/icons'
import { LoadingSpinier } from 'common/Loading'
import {
  BLOGS,
  PENDING
} from 'utils/constants'
import { postTypeStatus } from 'utils/helpers'
import { LikeButtonAnimation } from './styled'

const MotionStack = motion(Stack)

export const CommentsForPost = ({
  selectedUser,
  comments,
  getCommentsStatus,
  setEditCommentData,
  commentsOption,
  blogParent
}) => {
  const dispatch = useDispatch()
  const [animate, setAnimate] = useState([])
  const [loadingMore, setLoadingMore] = useState(false)
  const [oldCommentsCount, setOldCommentsCount] = useState(0)
  const observer = useRef()
  const secondToLastItem = useRef(null)

  const animateLike = (id) => {
    setAnimate((prevState) => ({
      ...prevState,
      [id]: !prevState[id]
    }))

    setTimeout(() => {
      setAnimate((prevState) => ({
        ...prevState,
        [id]: !prevState[id]
      }))
    }, 500)
  }

  const GoToMarker = (item) => {
    dispatch(setUserSectionPage('clear'))

    dispatch(setProfileItemId(''))

    setTimeout(() => {
      let coord = {
        coord: item?.coordinates,
        isStatic: true
      }
      dispatch(showCurrentChatFriendMarkerCoordinate(coord))

      sessionStorage.setItem('openProfileAfterAnimation', item?.profile_id)
    }, 850)
  }

  const goToHeaderOriginalPos = () => {
    document.querySelector(`#${BLOGS}-drawer-body`)?.scrollTo({
      top: 0,
      behavior: 'auto'
    })
  }

  const openCommentsOfComment = (item) => {
    goToHeaderOriginalPos()

    dispatch(setComments([]))
    dispatch(setBlogParent([...blogParent, item]))
    dispatch(getComments({
      type: 'comment',
      comment_id: item.comment_id
    }))
  }

  const cleanComments = () => {
    if (comments.length) {
      return comments.filter((c) => c?.profile_id)
    }

    return comments
  }

  const hasHiddenComments = () => {
    if (comments.length) {
      return comments.some((c) => !c?.profile_id)
    }

    return false
  }

  useEffect(() => {
    observer.current = new IntersectionObserver((entries) => {
      // The "10" is the limit set on the backend DB query
      if (
        entries[0].isIntersecting
        && comments.length - oldCommentsCount >= 10
      ) {
        observer.current.unobserve(secondToLastItem.current)
        setLoadingMore(true)
        setOldCommentsCount(comments.length)
        dispatch(getComments({
          ...commentsOption,
          lastItemId: comments.at(-1).comment_id
        }))
      }
    })

    const currentObserver = observer.current
    if (secondToLastItem.current && comments.length - oldCommentsCount >= 10) {
      currentObserver.observe(secondToLastItem.current)
    }

    setLoadingMore(false)

    return () => {
      if (secondToLastItem.current) {
        observer.current.disconnect()
      }
    }
  }, [comments])

  useEffect(() => {
    setOldCommentsCount(0)
  }, [blogParent])

  return (
    <Stack>
      {
        getCommentsStatus === PENDING && !comments.length
          ? (
            <Stack margin="70px 0 50px 0">
              <LoadingSpinier />
            </Stack>
          ) : ''
      }

      {
        hasHiddenComments()
          ? (
            <MotionStack
              initial={{ x: -450 }}
              animate={{ x: 0 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 60
              }}
              textAlign="center"
              fontSize="13px"
              backgroundColor={COLORS.gold_op8}
              borderRadius="3px"
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              gap="6px"
              padding="10px 0"
              overflow="hidden"
            >
              <InfoSquareButton width="20px" />
              <Text>Some comments are hidden based on block status</Text>
            </MotionStack>
          )
          : ''
      }

      {
        comments
        && comments.length
          ? (() => {
            const cleanComms = cleanComments()

            return cleanComms.map((item, index) => (
              <MotionStack
                key={item.comment_id}
                initial={{ x: -450 }}
                animate={{ x: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 60
                }}
                ref={cleanComms.length - 2 === index ? secondToLastItem : null}
                onClick={() => openCommentsOfComment(item)}
                cursor="pointer"
                backgroundColor="rgb(229, 229, 229)"
                borderRadius="3px"
                border="solid 1px black"
                padding="10px 5px"
                gap="1rem"
                display="flex"
                justifyContent="flex-start"
                pos="relative"
              >
                {
                  selectedUser.full_name === item.full_name
                    ? (
                      <Stack
                        width="15px"
                        height="15px"
                        cursor="pointer"
                        right="3"
                        pos="absolute"
                        onClick={(e) => {
                          e.stopPropagation()

                          setEditCommentData({})
                          setEditCommentData(item)
                          goToHeaderOriginalPos()
                        }}
                      >
                        <EditIcon2 />
                      </Stack>
                    ) : ''
                }

                <Stack
                  alignItems="center"
                  display="inline"
                  width="auto"
                  justifyContent="flex-start"
                  marginLeft="auto"
                  marginRight="auto"
                >
                  <Image
                    boxSize="55px"
                    position="relative"
                    objectFit="cover"
                    src={item.profile_picture || ImagesPng.DefaultImage}
                    alt="User Image"
                    borderRadius="full"
                    display="inline-flex"
                    verticalAlign="bottom"
                    cursor={item.coordinates && item.coordinates.length ? 'pointer' : 'auto'}
                    onClick={(e) => {
                      e.stopPropagation()

                      if (
                        item.coordinates
                        && item.coordinates.length
                      ) {
                        GoToMarker(item)
                      }
                    }}
                  />
                  <Text
                    display="inline-flex"
                    marginLeft="10px"
                    fontSize="16px"
                    lineHeight="28px"
                    wordBreak="break-word"
                  >
                    {item.full_name}
                    <br />
                    { postTypeStatus({ item, noLabel: true }) }
                  </Text>
                </Stack>

                <Stack width="98%" mx="auto">
                  <Text whiteSpace="pre-wrap">
                    {item.body}
                  </Text>
                </Stack>

                <Stack
                  width="98%"
                  height="29px"
                  direction="row"
                  border="solid 1px black"
                  borderRadius="5px"
                  padding="1.5px 15px"
                  justifyContent="space-around"
                  background={COLORS.whiteYellowGradientTopToBot}
                  gap="0"
                  mx="auto"
                >
                  <LikeButtonAnimation className={animate[item.comment_id] ? 'animate' : ''}>
                    <Stack
                      as="button"
                      onClick={(e) => {
                        e.stopPropagation()

                        if (!item?.likeOngoing) {
                          animateLike(item.comment_id)
                          dispatch(setBlogLikeOngoing({
                            id: item.comment_id,
                            type: 'comment'
                          }))
                          dispatch(likeComment(item.comment_id))
                        }
                      }}
                    >
                      <LikeIcon fill={item.likedByMe ? COLORS.gold : 'none'} />
                    </Stack>

                    <Text>{item.likes}</Text>
                  </LikeButtonAnimation>

                  <Stack
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                  >
                    <Stack
                      as="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        openCommentsOfComment(item)
                      }}
                    >
                      <CommentIcon />
                    </Stack>
                    <Text>{item.comments}</Text>
                  </Stack>

                  <Stack
                    display="none"
                    flexDirection="row"
                    alignItems="center"
                  >
                    <RepostIcon />
                    <Text>0</Text>
                  </Stack>

                  <Stack
                    display="none"
                    flexDirection="row"
                    alignItems="center"
                  >
                    <ShareIcon />
                    <Text>0</Text>
                  </Stack>
                </Stack>
              </MotionStack>
            ))
          })()
          : (
            <Stack>
              {
                getCommentsStatus === PENDING && !comments.length
                  ? ''
                  : (
                    <MotionStack
                      key="no-comment-placeholder"
                      initial={{ x: -450 }}
                      animate={{ x: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 60
                      }}
                    >
                      <Text
                        textAlign="center"
                        backgroundColor="rgb(229, 229, 229)"
                        borderRadius="3px"
                        border="solid 1px black"
                        padding="10px"
                      >
                        Be the first one to comment
                      </Text>
                    </MotionStack>
                  )
              }
            </Stack>
          )
      }
      {
        loadingMore && getCommentsStatus === PENDING
          ? (
            <Stack margin="75px 0 50px 0">
              <LoadingSpinier />
            </Stack>
          ) : ''
      }
    </Stack>
  )
}
